import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { ROLE_LABELS, ROLE_OPTIONS, canEditUserRoles } from './aivisRoles';
import { clearbitLogoFromWebsite } from '../../utils/clearbitLogo';
import './ai-vis.css';

const POLL_MS = 5000;
const PAGE_SIZE = 15;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function initialsFor(u) {
  const name = (u.display_name || u.email || u.username || '').trim();
  if (!name) return '?';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function LockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function AdminConsolePage() {
  const { user: currentUser } = useAuth();
  const canEditRoles = canEditUserRoles(currentUser);

  const [users, setUsers] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userNext, setUserNext] = useState(null);
  const [userPrevious, setUserPrevious] = useState(null);

  // Users table sorting. sortBy is one of 'name' | 'role' | null; sortDir is 'asc' | 'desc'.
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const [pendingInvites, setPendingInvites] = useState([]);

  const [orgs, setOrgs] = useState([]);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('company_member');
  const [inviteOrgIds, setInviteOrgIds] = useState([]);
  const [inviteSelectedIds, setInviteSelectedIds] = useState([]);
  const [inviteCampaigns, setInviteCampaigns] = useState([]);
  const [inviteCampaignsError, setInviteCampaignsError] = useState('');
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [pickerRect, setPickerRect] = useState(null);
  const clientPickerRef = useRef(null);
  const clientTriggerRef = useRef(null);

  // Per-row org assignment state: { [userId]: orgId }
  const [rowOrgEdit, setRowOrgEdit] = useState({});
  const [rowOrgBusy, setRowOrgBusy] = useState({});

  // Per-row role editing state
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [roleBusy, setRoleBusy] = useState({});

  // Combined Deactivate/Delete modal for active users
  const [removeModal, setRemoveModal] = useState(null); // { user }
  const [removeChoice, setRemoveChoice] = useState('deactivate'); // 'deactivate' | 'delete'
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState('');

  // Simple confirm dialog — used for revoking invites and hard-deleting an
  // already-inactive user (deactivate isn't a relevant choice there).
  const [confirmDialog, setConfirmDialog] = useState(null);

  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [toast, setToast] = useState(null);
  const [toastVisible, setToastVisible] = useState(false);
  const toastVisibleRef = useRef(false);

  const showToast = useCallback((message, tone = 'success') => {
    toastVisibleRef.current = false;
    setToastVisible(false);
    setToast({ id: Date.now(), message, tone });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(userSearch), 400);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setUserPage(1);
  }, [debouncedSearch, statusFilter, sortBy, sortDir]);

  const loadUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(userPage) });
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (sortBy) params.set('ordering', sortDir === 'desc' ? `-${sortBy}` : sortBy);
      const { data } = await api.get(`/aivis/admin/users/?${params.toString()}`);
      if (data.results) {
        setUsers(data.results);
        setUserCount(data.count ?? data.results.length);
        setUserNext(data.next || null);
        setUserPrevious(data.previous || null);
      } else {
        const list = Array.isArray(data) ? data : [];
        setUsers(list);
        setUserCount(list.length);
        setUserNext(null);
        setUserPrevious(null);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load users.');
    }
  }, [userPage, debouncedSearch, statusFilter, sortBy, sortDir]);

  const toggleSort = useCallback((key) => {
    setSortBy((cur) => {
      if (cur !== key) {
        setSortDir('asc');
        return key;
      }
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return cur;
    });
  }, []);

  const loadInvitations = useCallback(async () => {
    try {
      const { data } = await api.get('/aivis/admin/invitations/');
      setPendingInvites(Array.isArray(data.pending_invitations) ? data.pending_invitations : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load invitations.');
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadUsers(), loadInvitations()]);
  }, [loadUsers, loadInvitations]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  // Load organizations once on mount (needed for invite org picker + row org assignment)
  useEffect(() => {
    api.get('/aivis/organizations/').then(({ data }) => {
      const list = data.results ?? (Array.isArray(data) ? data : []);
      setOrgs(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      refreshAll();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [refreshAll]);

  useEffect(() => {
    if (!inviteOpen) return undefined;
    let cancelled = false;
    setInviteCampaignsError('');
    (async () => {
      try {
        // Fetch ALL campaigns — paginate through to get complete list for system admins.
        let allCampaigns = [];
        let url = '/aivis/campaigns/?page_size=200';
        while (url) {
          const { data } = await api.get(url);
          const results = data.results ?? (Array.isArray(data) ? data : []);
          allCampaigns = allCampaigns.concat(results);
          // DRF returns an absolute `next` URL (e.g. https://host/api/aivis/...).
          // Strip the origin AND the leading `/api` so it is relative to the
          // axios instance's baseURL ('/api'); otherwise it becomes
          // `/api/api/...`, which the API-key middleware rejects with a 401.
          if (data.next) {
            let next = data.next.replace(/^https?:\/\/[^/]+/, '');
            if (next.startsWith('/api/')) next = next.slice(4);
            url = next;
          } else {
            url = null;
          }
        }
        if (!cancelled) setInviteCampaigns(allCampaigns);
      } catch (err) {
        if (!cancelled) {
          setInviteCampaigns([]);
          setInviteCampaignsError(err.response?.data?.detail || 'Could not load clients.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [inviteOpen]);


  useEffect(() => {
    const lock = inviteOpen || confirmDialog !== null || removeModal !== null;
    if (!lock) return undefined;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbar > 0) {
      document.body.style.paddingRight = `${scrollbar}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [inviteOpen, confirmDialog, removeModal]);

  useEffect(() => {
    toastVisibleRef.current = toastVisible;
  }, [toastVisible]);

  useEffect(() => {
    if (!toast) {
      toastVisibleRef.current = false;
      setToastVisible(false);
      return undefined;
    }
    toastVisibleRef.current = false;
    setToastVisible(false);
    let cancelled = false;
    const enter = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (cancelled) return;
        toastVisibleRef.current = true;
        setToastVisible(true);
      });
    });
    const hideTimer = window.setTimeout(() => {
      if (cancelled) return;
      toastVisibleRef.current = false;
      setToastVisible(false);
    }, 5200);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(enter);
      window.clearTimeout(hideTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast?.id]);

  const onToastTransitionEnd = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== 'transform' && e.propertyName !== 'opacity') return;
    if (!toastVisibleRef.current) {
      setToast(null);
    }
  }, []);

  async function sendInvite(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const body = {
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
      };
      if (inviteOrgIds.length > 0) body.organization_ids = inviteOrgIds;
      if (inviteRole === 'campaign_viewer' && inviteSelectedIds.length > 0) {
        body.campaign_ids = inviteSelectedIds;
      }
      await api.post('/aivis/admin/users/invite/', body);
      showToast('Invitation sent.');
      setInviteEmail('');
      setInviteRole('company_member');
      setInviteOrgIds([]);
      setInviteSelectedIds([]);
      setClientSearch('');
      setClientPickerOpen(false);
      setInviteCampaignsError('');
      setInviteOpen(false);
      await refreshAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invite failed.');
    } finally {
      setBusy(false);
    }
  }

  function revokeInvite(invId, email) {
    setConfirmDialog({ type: 'revoke', id: invId, email });
  }

  async function reactivateUser(u) {
    if (u.is_system_protected) return;
    try {
      await api.patch(`/aivis/admin/users/${u.id}/`, { is_active: true });
      showToast(`${u.display_name || u.email || u.username} was reactivated.`);
      await refreshAll();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not reactivate user.', 'error');
    }
  }

  function openRemoveModal(u) {
    if (u.is_system_protected) return;
    setRemoveError('');
    setRemoveChoice('deactivate');
    setRemoveModal(u);
  }

  async function confirmRemove() {
    if (!removeModal) return;
    setRemoveError('');
    setRemoveBusy(true);
    const label = removeModal.display_name || removeModal.email || removeModal.username;
    try {
      if (removeChoice === 'delete') {
        await api.delete(`/aivis/admin/users/${removeModal.id}/`);
        showToast(`${label} was deleted.`);
      } else {
        await api.patch(`/aivis/admin/users/${removeModal.id}/`, { is_active: false });
        showToast(`${label} was deactivated.`);
      }
      setRemoveModal(null);
      await refreshAll();
    } catch (err) {
      setRemoveError(
        err.response?.data?.detail
          || (removeChoice === 'delete' ? 'Delete failed.' : 'Could not deactivate user.')
      );
    } finally {
      setRemoveBusy(false);
    }
  }

  function hardDeleteUser(u) {
    if (u.is_system_protected) return;
    setConfirmDialog({ type: 'delete', user: u });
  }

  async function changeUserRole(u, newRole) {
    if (newRole === u.role) {
      setEditingRoleId(null);
      return;
    }
    // Client Viewer is a terminal role — blocked both ways (matches backend).
    if (newRole === 'campaign_viewer' || u.role === 'campaign_viewer') {
      showToast('Client Viewer role cannot be changed from the Members table. It is set at invite time only.', 'error');
      setEditingRoleId(null);
      return;
    }
    setRoleBusy((prev) => ({ ...prev, [u.id]: true }));
    try {
      await api.patch(`/aivis/admin/users/${u.id}/`, { role: newRole });
      showToast(`${u.display_name || u.email || u.username} is now ${ROLE_LABELS[newRole] || newRole}.`);
      setEditingRoleId(null);
      await refreshAll();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not update role.', 'error');
    } finally {
      setRoleBusy((prev) => { const n = { ...prev }; delete n[u.id]; return n; });
    }
  }

  async function assignOrgToUser(u, orgIds) {
    setRowOrgBusy((prev) => ({ ...prev, [u.id]: true }));
    try {
      await api.patch(`/aivis/admin/users/${u.id}/`, { organization_ids: orgIds });
      await refreshAll();
      setRowOrgEdit((prev) => { const n = { ...prev }; delete n[u.id]; return n; });
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not update organization.', 'error');
    } finally {
      setRowOrgBusy((prev) => { const n = { ...prev }; delete n[u.id]; return n; });
    }
  }

  async function executeConfirm() {
    if (!confirmDialog) return;
    setError('');
    setBusy(true);
    try {
      if (confirmDialog.type === 'revoke') {
        await api.delete(`/aivis/admin/invitations/${confirmDialog.id}/`);
        showToast('Invitation revoked.');
      } else if (confirmDialog.type === 'delete') {
        const u = confirmDialog.user;
        await api.delete(`/aivis/admin/users/${u.id}/`);
        showToast(`${u.display_name || u.email || u.username} was deleted.`);
      }
      setConfirmDialog(null);
      await refreshAll();
    } catch (err) {
      setError(
        err.response?.data?.detail
          || (confirmDialog.type === 'revoke' ? 'Could not revoke invitation.' : 'Delete failed.')
      );
    } finally {
      setBusy(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil((userCount || 0) / PAGE_SIZE));

  return (
    <>
      {/* ── Members ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
        <div>
          <h1 className="aivis-page-title" style={{ marginBottom: 4 }}>
            Members
          </h1>
          <p className="aivis-page-sub" style={{ marginBottom: 0 }}>
            Invite teammates and manage roles. System admins cannot be removed or deactivated.
          </p>
        </div>
        <button
          type="button"
          className="aivis-btn-primary"
          style={{ width: 'auto', minWidth: 120 }}
          onClick={() => { setInviteOpen(true); setError(''); }}
        >
          Invite user
        </button>
      </div>

      {error && <div className="aivis-auth-error">{error}</div>}

      <div className="aivis-card" style={{ marginBottom: 20 }}>
        <h2 className="aivis-page-title" style={{ fontSize: 18, marginBottom: 12 }}>
          Users
        </h2>
        <div className="aivis-members-toolbar">
          <div className="aivis-field" style={{ margin: 0, maxWidth: 320, flex: '1 1 240px' }}>
            <label htmlFor="aivis-user-search">Search</label>
            <input
              id="aivis-user-search"
              type="search"
              placeholder="Name or email"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="aivis-segmented" role="tablist" aria-label="Filter by status">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={statusFilter === opt.value}
                className={`aivis-segmented-option${statusFilter === opt.value ? ' aivis-segmented-option--active' : ''}`}
                onClick={() => setStatusFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="aivis-table-wrap">
          <table className="aivis-table">
            <thead>
              <tr>
                <th>
                  <button
                    type="button"
                    className="aivis-sort-th"
                    onClick={() => toggleSort('name')}
                    aria-sort={sortBy === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    Name
                    <span className="aivis-sort-arrow" aria-hidden>
                      {sortBy === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </button>
                </th>
                <th style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="aivis-sort-th"
                    onClick={() => toggleSort('role')}
                    aria-sort={sortBy === 'role' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    Role
                    <span className="aivis-sort-arrow" aria-hidden>
                      {sortBy === 'role' ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </button>
                </th>
                <th style={{ textAlign: 'center' }}>Organization</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center' }}>Joined</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const canEditThisRole = canEditRoles
                  && !u.is_system_protected
                  && u.id !== currentUser?.id
                  && u.role !== 'campaign_viewer';
                return (
                  <tr key={u.id}>
                    <td>
                      <div className="aivis-user-cell">
                        <span className="aivis-avatar-circle" aria-hidden>{initialsFor(u)}</span>
                        <div className="aivis-user-cell-text">
                          <span className="aivis-user-cell-name">{u.display_name || u.email || '—'}</span>
                          <span className="aivis-user-cell-email">{u.email || u.username}</span>
                        </div>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {editingRoleId === u.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <select
                            autoFocus
                            className="aivis-role-select"
                            defaultValue={u.role}
                            disabled={!!roleBusy[u.id]}
                            onChange={(e) => changeUserRole(u, e.target.value)}
                          >
                            {ROLE_OPTIONS
                              .filter((opt) => opt.value !== 'campaign_viewer')
                              .map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                          </select>
                          <button
                            type="button"
                            className="aivis-btn-small"
                            disabled={!!roleBusy[u.id]}
                            onClick={() => setEditingRoleId(null)}
                            aria-label="Cancel role edit"
                          >✕</button>
                        </div>
                      ) : canEditThisRole ? (
                        <button
                          type="button"
                          className="aivis-role-badge-btn"
                          title="Click to change role"
                          onClick={() => setEditingRoleId(u.id)}
                        >
                          <span className={u.role === 'company_member' || u.role === 'campaign_viewer' ? 'aivis-badge aivis-badge-muted' : 'aivis-badge'}>
                            {ROLE_LABELS[u.role] || u.role || '—'}
                          </span>
                        </button>
                      ) : (
                        <span className={u.role === 'company_member' || u.role === 'campaign_viewer' ? 'aivis-badge aivis-badge-muted' : 'aivis-badge'}>
                          {ROLE_LABELS[u.role] || u.role || '—'}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {u.is_system_protected ? (
                        <span style={{ color: 'var(--aivis-text-muted)', fontSize: 13 }}>—</span>
                      ) : rowOrgEdit[u.id] !== undefined ? (
                        <div style={{ minWidth: 180, textAlign: 'left', display: 'inline-block' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
                            {orgs.map((o) => {
                              const checked = (rowOrgEdit[u.id] || []).includes(o.id);
                              return (
                                <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={rowOrgBusy[u.id]}
                                    onChange={() => {
                                      setRowOrgEdit((prev) => {
                                        const cur = prev[u.id] || [];
                                        return { ...prev, [u.id]: checked ? cur.filter((id) => id !== o.id) : [...cur, o.id] };
                                      });
                                    }}
                                    style={{ accentColor: 'var(--aivis-accent)' }}
                                  />
                                  {o.name}
                                </label>
                              );
                            })}
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              type="button"
                              className="aivis-btn-small"
                              disabled={rowOrgBusy[u.id]}
                              onClick={() => assignOrgToUser(u, rowOrgEdit[u.id] || [])}
                            >Save</button>
                            <button
                              type="button"
                              className="aivis-btn-small"
                              disabled={rowOrgBusy[u.id]}
                              onClick={() => setRowOrgEdit((prev) => { const n = { ...prev }; delete n[u.id]; return n; })}
                            >✕</button>
                          </div>
                        </div>
                      ) : u.role === 'campaign_viewer' ? (
                        <span style={{ fontSize: 13 }}>
                          {(u.campaigns || []).length > 0
                            ? (u.campaigns || []).map((c) => c.name).join(', ')
                            : <span style={{ color: 'var(--aivis-text-muted)' }}>No client assigned</span>
                          }
                        </span>
                      ) : (
                        <button
                          type="button"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: 0 }}
                          title="Click to assign organizations"
                          onClick={() => {
                            const currentIds = (u.organizations || []).map((o) => o.id);
                            setRowOrgEdit((prev) => ({ ...prev, [u.id]: currentIds }));
                          }}
                        >
                          {(u.organizations || []).length > 0
                            ? (u.organizations || []).map((o) => o.name).join(', ')
                            : <span style={{ color: 'var(--aivis-accent)', textDecoration: 'underline dotted' }}>Assign org</span>
                          }
                        </button>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={u.is_active ? 'aivis-status-capsule aivis-status-capsule--active' : 'aivis-status-capsule aivis-status-capsule--inactive'}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>{u.date_joined ? new Date(u.date_joined).toLocaleDateString() : '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {u.is_system_protected ? (
                        <span className="aivis-protected-label" style={{ justifyContent: 'center' }}>
                          <LockIcon /> Protected
                        </span>
                      ) : u.is_active ? (
                        <div className="aivis-row-actions" style={{ justifyContent: 'center' }}>
                          <button
                            type="button"
                            className="aivis-btn-small aivis-btn-revoke"
                            onClick={() => openRemoveModal(u)}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="aivis-row-actions" style={{ justifyContent: 'center' }}>
                          <button
                            type="button"
                            className="aivis-btn-small"
                            onClick={() => reactivateUser(u)}
                          >
                            Reactivate
                          </button>
                          <button
                            type="button"
                            className="aivis-btn-small aivis-btn-revoke"
                            onClick={() => hardDeleteUser(u)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {userCount > PAGE_SIZE && (
          <div className="aivis-pagination">
            <span className="aivis-page-sub" style={{ margin: 0, fontSize: 13 }}>
              Page {userPage} of {totalPages} ({userCount} users)
            </span>
            <div className="aivis-pagination-btns">
              <button
                type="button"
                className="aivis-btn-secondary"
                disabled={!userPrevious}
                onClick={() => setUserPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="aivis-btn-secondary"
                disabled={!userNext}
                onClick={() => setUserPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="aivis-card" style={{ marginBottom: 20 }}>
        <h2 className="aivis-page-title" style={{ fontSize: 18, marginBottom: 12 }}>
          Pending invites
        </h2>
        {pendingInvites.length === 0 ? (
          <p className="aivis-page-sub" style={{ margin: 0 }}>
            No pending invitations.
          </p>
        ) : (
          <div className="aivis-table-wrap">
            <table className="aivis-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Campaign</th>
                  <th>Invited by</th>
                  <th>Sent</th>
                  <th>Expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.email}</td>
                    <td>{ROLE_LABELS[inv.invited_role] || inv.invited_role}</td>
                    <td>{inv.campaign_name || '—'}</td>
                    <td>
                      {inv.invited_by_name || '—'}
                    </td>
                    <td>{inv.created_at ? new Date(inv.created_at).toLocaleString() : '—'}</td>
                    <td>{inv.expires_at ? new Date(inv.expires_at).toLocaleString() : '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="aivis-btn-small aivis-btn-revoke"
                        onClick={() => revokeInvite(inv.id, inv.email)}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Combined Deactivate / Delete choice modal (active users only) */}
      {removeModal && (
        <div
          className="aivis-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="aivis-remove-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !removeBusy) setRemoveModal(null);
          }}
        >
          <div className="aivis-modal aivis-org-modal">
            <div className="aivis-org-del-header">
              <div className="aivis-org-del-icon" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <h2 id="aivis-remove-title" className="aivis-org-modal-title">
                Remove {removeModal.display_name || removeModal.email || removeModal.username}?
              </h2>
            </div>
            <p className="aivis-modal-body">
              Choose what should happen to this account:
            </p>
            <div className="aivis-org-del-options">
              <label className={`aivis-org-del-option ${removeChoice === 'deactivate' ? 'aivis-org-del-option--active' : ''}`}>
                <input
                  type="radio"
                  name="remove-user-mode"
                  value="deactivate"
                  checked={removeChoice === 'deactivate'}
                  onChange={() => setRemoveChoice('deactivate')}
                />
                <div className="aivis-org-del-option-body">
                  <span className="aivis-org-del-option-title">Deactivate — keep history</span>
                  <span className="aivis-org-del-option-desc">
                    Blocks sign-in and hides them from active use, but preserves their account and
                    activity. Reversible anytime via "Reactivate".
                  </span>
                </div>
              </label>
              <label className={`aivis-org-del-option aivis-org-del-option--danger ${removeChoice === 'delete' ? 'aivis-org-del-option--active' : ''}`}>
                <input
                  type="radio"
                  name="remove-user-mode"
                  value="delete"
                  checked={removeChoice === 'delete'}
                  onChange={() => setRemoveChoice('delete')}
                />
                <div className="aivis-org-del-option-body">
                  <span className="aivis-org-del-option-title">Delete permanently</span>
                  <span className="aivis-org-del-option-desc">
                    Removes the user account entirely. <strong>This cannot be undone.</strong>
                  </span>
                </div>
              </label>
            </div>
            {removeError && <div className="aivis-auth-error" style={{ margin: '12px 0 0' }}>{removeError}</div>}
            <div className="aivis-wizard-actions aivis-wizard-actions--compact" style={{ marginTop: 20 }}>
              <button type="button" className="aivis-btn-secondary" disabled={removeBusy} onClick={() => setRemoveModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={removeChoice === 'delete' ? 'aivis-btn-danger' : 'aivis-btn-primary'}
                disabled={removeBusy}
                onClick={confirmRemove}
              >
                {removeBusy
                  ? (removeChoice === 'delete' ? 'Deleting…' : 'Deactivating…')
                  : (removeChoice === 'delete' ? 'Delete permanently' : 'Deactivate user')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDialog && (
        <div
          className="aivis-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="aivis-confirm-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setConfirmDialog(null);
          }}
        >
          <div className="aivis-modal">
            <h2 id="aivis-confirm-title" className="aivis-page-title" style={{ fontSize: 18, marginBottom: 12 }}>
              {confirmDialog.type === 'revoke' ? 'Revoke invitation?' : 'Delete user?'}
            </h2>
            <p className="aivis-modal-body">
              {confirmDialog.type === 'revoke' ? (
                <>
                  The invite for <strong style={{ color: 'var(--aivis-text)' }}>{confirmDialog.email}</strong> will no longer
                  work. You can send a new invite later if needed.
                </>
              ) : (
                <>
                  Delete <strong style={{ color: 'var(--aivis-text)' }}>{confirmDialog.user.email || confirmDialog.user.username}</strong>?
                  This cannot be undone.
                </>
              )}
            </p>
            <div className="aivis-wizard-actions" style={{ marginTop: 0 }}>
              <button type="button" className="aivis-btn-secondary" disabled={busy} onClick={() => setConfirmDialog(null)}>
                Cancel
              </button>
              <button type="button" className="aivis-btn-danger" disabled={busy} onClick={executeConfirm}>
                {busy ? 'Please wait…' : confirmDialog.type === 'revoke' ? 'Revoke' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {inviteOpen && (
        <div
          className="aivis-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="aivis-invite-title"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) { setInviteOpen(false); setClientPickerOpen(false); } }}
        >
          <div className="aivis-invite-modal">
            {/* Header */}
            <div className="aivis-invite-modal-header">
              <div className="aivis-invite-modal-header-icon" aria-hidden>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="19" y1="8" x2="19" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="22" y1="11" x2="16" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div>
                <h2 id="aivis-invite-title" className="aivis-invite-modal-title">Invite Member</h2>
                <p className="aivis-invite-modal-subtitle">Send an invitation link via email</p>
              </div>
              <button
                type="button"
                className="aivis-invite-modal-close"
                aria-label="Close"
                onClick={() => { setInviteOpen(false); setClientPickerOpen(false); }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <form onSubmit={sendInvite} className="aivis-invite-modal-body">
              {/* Email */}
              <div className="aivis-invite-field">
                <label htmlFor="aivis-invite-modal-email" className="aivis-invite-label">Email address</label>
                <input
                  id="aivis-invite-modal-email"
                  type="email"
                  className="aivis-invite-input"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                />
              </div>

              {/* Role */}
              <div className="aivis-invite-field">
                <label className="aivis-invite-label">Role</label>
                <div className="aivis-invite-role-group">
                  {[
                    { value: 'company_member', label: 'Company member', desc: 'Manage campaigns, cannot delete' },
                    { value: 'company_admin', label: 'Company admin', desc: 'Full access incl. Members & Settings' },
                    { value: 'campaign_viewer', label: 'Client viewer', desc: 'Read-only access to selected clients' },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`aivis-invite-role-card${inviteRole === opt.value ? ' aivis-invite-role-card--active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="aivis-invite-role"
                        value={opt.value}
                        checked={inviteRole === opt.value}
                        onChange={(e) => {
                          setInviteRole(e.target.value);
                          if (e.target.value !== 'campaign_viewer') {
                            setInviteSelectedIds([]);
                            setClientPickerOpen(false);
                          }
                          if (e.target.value === 'campaign_viewer') {
                            setInviteOrgIds([]);
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                      <span className="aivis-invite-role-card-label">{opt.label}</span>
                      <span className="aivis-invite-role-card-desc">{opt.desc}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Organization multi-select — shown for company_member and company_admin
                  (client viewers pick specific clients instead, below). This selection
                  is carried over automatically when the invite is accepted, so the org
                  never needs to be re-assigned by hand afterwards. */}
              {(inviteRole === 'company_member' || inviteRole === 'company_admin') && orgs.length > 0 && (
                <div className="aivis-invite-field">
                  <label className="aivis-invite-label">
                    Organization access
                    {inviteRole === 'company_member' && <span style={{ color: 'var(--aivis-danger)', marginLeft: 4 }}>*</span>}
                    {inviteOrgIds.length > 0 && (
                      <span className="aivis-invite-label-count">{inviteOrgIds.length} selected</span>
                    )}
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                    {orgs.map((o) => {
                      const checked = inviteOrgIds.includes(o.id);
                      return (
                        <label
                          key={o.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                            padding: '8px 12px', borderRadius: 8,
                            border: `1.5px solid ${checked ? 'var(--aivis-accent)' : 'var(--aivis-border)'}`,
                            background: checked ? 'var(--aivis-accent-soft)' : 'var(--surface, #fff)',
                            transition: 'border-color 0.15s, background 0.15s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setInviteOrgIds((prev) =>
                                checked ? prev.filter((id) => id !== o.id) : [...prev, o.id]
                              );
                            }}
                            style={{ accentColor: 'var(--aivis-accent)', width: 16, height: 16 }}
                          />
                          <span style={{ fontSize: 14, fontWeight: checked ? 600 : 400 }}>{o.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Client access multi-picker */}
              {inviteRole === 'campaign_viewer' && (() => {
                // Group campaigns by organization
                const orgs = {};
                inviteCampaigns.forEach((c) => {
                  const key = c.organization_name || 'No organization';
                  if (!orgs[key]) orgs[key] = [];
                  orgs[key].push(c);
                });
                const filtered = Object.entries(orgs).reduce((acc, [org, list]) => {
                  const q = clientSearch.toLowerCase();
                  const matches = list.filter(
                    (c) => !q
                      || (c.client_name || '').toLowerCase().includes(q)
                      || (c.name || '').toLowerCase().includes(q)
                      || org.toLowerCase().includes(q),
                  );
                  if (matches.length) acc.push([org, matches]);
                  return acc;
                }, []);

                return (
                  <div className="aivis-invite-field" ref={clientPickerRef}>
                    <label className="aivis-invite-label">
                      Client access
                      {inviteSelectedIds.length > 0 && (
                        <span className="aivis-invite-label-count">{inviteSelectedIds.length} selected</span>
                      )}
                    </label>

                    {/* Selected chips */}
                    {inviteSelectedIds.length > 0 && (
                      <div className="aivis-invite-chips">
                        {inviteSelectedIds.map((id) => {
                          const c = inviteCampaigns.find((x) => x.id === id);
                          if (!c) return null;
                          const logo = c.client_logo_url || clearbitLogoFromWebsite(c.client_website);
                          return (
                            <span key={id} className="aivis-invite-chip">
                              {logo && <img src={logo} alt="" className="aivis-invite-chip-logo" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                              {c.client_name || c.name}
                              <button
                                type="button"
                                className="aivis-invite-chip-remove"
                                aria-label={`Remove ${c.client_name || c.name}`}
                                onClick={() => setInviteSelectedIds((ids) => ids.filter((x) => x !== id))}
                              >×</button>
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Picker trigger */}
                    <button
                      type="button"
                      className="aivis-invite-picker-trigger"
                      ref={clientTriggerRef}
                      onClick={() => {
                        if (!clientPickerOpen && clientTriggerRef.current) {
                          setPickerRect(clientTriggerRef.current.getBoundingClientRect());
                        }
                        setClientPickerOpen((v) => !v);
                      }}
                      aria-expanded={clientPickerOpen}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden style={{ opacity: 0.5 }}>
                        <path d="M21 21l-4.35-4.35M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <input
                        className="aivis-invite-picker-search"
                        placeholder={inviteSelectedIds.length === 0 ? 'Search and select clients…' : 'Add more clients…'}
                        value={clientSearch}
                        onChange={(e) => {
                          setClientSearch(e.target.value);
                          if (!clientPickerOpen && clientTriggerRef.current) {
                            setPickerRect(clientTriggerRef.current.getBoundingClientRect());
                          }
                          setClientPickerOpen(true);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!clientPickerOpen && clientTriggerRef.current) {
                            setPickerRect(clientTriggerRef.current.getBoundingClientRect());
                          }
                          setClientPickerOpen(true);
                        }}
                      />
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden style={{ opacity: 0.4, flexShrink: 0 }}>
                        <path d={clientPickerOpen ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>

                    {/* Invisible backdrop — closes the dropdown when clicking anywhere outside it */}
                    {clientPickerOpen && (
                      <div
                        style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
                        onMouseDown={() => setClientPickerOpen(false)}
                      />
                    )}

                    {/* Dropdown — fixed-position so it is never clipped by the modal */}
                    {clientPickerOpen && (
                      <div
                        className="aivis-invite-picker-dropdown"
                        style={pickerRect ? {
                          top: pickerRect.bottom + 4,
                          left: pickerRect.left,
                          width: pickerRect.width,
                        } : undefined}
                      >
                        {inviteCampaignsError ? (
                          <div className="aivis-invite-picker-empty" style={{ color: 'var(--aivis-danger)' }}>
                            {inviteCampaignsError}
                          </div>
                        ) : filtered.length === 0 ? (
                          <div className="aivis-invite-picker-empty">No clients found</div>
                        ) : (
                          filtered.map(([org, list]) => (
                            <div key={org} className="aivis-invite-picker-group">
                              <div className="aivis-invite-picker-group-label">{org}</div>
                              {list.map((c) => {
                                const selected = inviteSelectedIds.includes(c.id);
                                const logo = c.client_logo_url || clearbitLogoFromWebsite(c.client_website);
                                return (
                                  <button
                                    key={c.id}
                                    type="button"
                                    className={`aivis-invite-picker-item${selected ? ' aivis-invite-picker-item--selected' : ''}`}
                                    onClick={() => {
                                      setInviteSelectedIds((ids) =>
                                        selected ? ids.filter((x) => x !== c.id) : [...ids, c.id],
                                      );
                                    }}
                                  >
                                    <div className="aivis-invite-picker-item-logo">
                                      {logo ? (
                                        <img src={logo} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                      ) : (
                                        <span className="aivis-invite-picker-item-logo-fallback">
                                          {(c.client_name || c.name || '?').slice(0, 1).toUpperCase()}
                                        </span>
                                      )}
                                    </div>
                                    <div className="aivis-invite-picker-item-text">
                                      <span className="aivis-invite-picker-item-name">{c.client_name || c.name}</span>
                                      {c.client_website && (
                                        <span className="aivis-invite-picker-item-url">{c.client_website.replace(/^https?:\/\/(www\.)?/, '')}</span>
                                      )}
                                    </div>
                                    {selected && (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden className="aivis-invite-picker-item-check">
                                        <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Actions */}
              <div className="aivis-invite-modal-actions">
                <button
                  type="button"
                  className="aivis-btn-secondary"
                  onClick={() => { setInviteOpen(false); setClientPickerOpen(false); }}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="aivis-btn-primary aivis-invite-submit"
                  disabled={busy || (inviteRole === 'campaign_viewer' && inviteSelectedIds.length === 0)}
                >
                  {busy ? 'Sending…' : `Send invite${inviteSelectedIds.length > 1 ? ` (${inviteSelectedIds.length} clients)` : ''}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast
        ? createPortal(
            <div
              className={`aivis-toast-anchor ${toastVisible ? 'aivis-toast-anchor--visible' : ''}`}
              onTransitionEnd={onToastTransitionEnd}
            >
              <div className={`aivis-toast${toast.tone === 'error' ? ' aivis-toast--error' : ''}`} role="status" aria-live="polite">
                <p className="aivis-toast-text">{toast.message}</p>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
